import {
    AccountStatus,
    AccountsAccountRow,
    AccountsCell,
    AccountsRow,
    AccountsStatusCounts,
} from '../components/accounts-overview/accounts-overview.types';
import { IProjectAccount } from '../models/project-account.model';
import { IStatementLayout, StatementNode } from '../models/statement-layout.model';
import { UNASSIGNED_GROUP_ID, byAccountNumber } from './accounts-presentation';
import { PeriodPick, StatementValues, sumOfAccounts } from './evaluate-statement';

const NOTHING = 0;

/**
 * The rows of a statement, read off its layout: what both presenters share.
 *
 * The layout decides the order and the lines; the presenter decides the cells and what an account
 * row carries. Visibility follows three rules:
 *
 *   - an empty position (no account loaded) is not rendered;
 *   - search and chips hide ACCOUNT rows, and a position with no visible account goes with them;
 *   - a group with nothing left to show renders nothing, not a heading and a total over nothing.
 *
 * Figures never follow visibility: they come from `values`, evaluated over every account.
 */
export interface StatementRowsInput {
    readonly layout: IStatementLayout;
    /** Every account of the statement. */
    readonly accounts: readonly IProjectAccount[];
    /** The evaluated layout (`evaluateStatement`). */
    readonly values: StatementValues;
    readonly periods: readonly PeriodPick[];
    readonly isVisible: (account: IProjectAccount) => boolean;
    readonly collapsed: ReadonlySet<string>;
    /** The cells of any row, from its values per period. */
    readonly cellsOf: (values: readonly (number | null)[]) => AccountsCell[];
    readonly accountRowOf: (account: IProjectAccount) => AccountsAccountRow;
    /** Set where accounts carry a status: each position then counts its accounts per status. */
    readonly statusOf?: (account: IProjectAccount) => AccountStatus;
    /** A position's label; `null` is the bucket of accounts without one. */
    readonly labelOf: (position: string | null) => string;
    /** Resolves a layout label key. */
    readonly layoutLabelOf: (key: string) => string;
    /** Set on the balance sheet: its root groups render as section rows with this label. */
    readonly sectionLabelOf?: (heading: string) => string;
}

export function statementRowsOf(input: StatementRowsInput): AccountsRow[] {
    const byPosition = new Map<string, IProjectAccount[]>();
    const unassigned: IProjectAccount[] = [];
    for (const account of input.accounts) {
        if (account.accountGroup === null) {
            unassigned.push(account);
            continue;
        }
        const bucket = byPosition.get(account.accountGroup);
        if (bucket === undefined) {
            byPosition.set(account.accountGroup, [account]);
        } else {
            bucket.push(account);
        }
    }

    const valuesOf = (id: string): readonly (number | null)[] => input.values.get(id) ?? input.periods.map(() => null);

    const positionRows = (
        id: string,
        position: string | null,
        accounts: readonly IProjectAccount[],
        values: readonly (number | null)[],
    ): AccountsRow[] => {
        const visible = accounts.filter(input.isVisible).sort(byAccountNumber);
        if (visible.length === NOTHING) {
            return [];
        }
        const isExpanded = !input.collapsed.has(id);
        const statusOf = input.statusOf;
        const rows: AccountsRow[] = [
            {
                kind: 'group',
                id,
                label: input.labelOf(position),
                isExpanded,
                values: input.cellsOf(values),
                ...(statusOf === undefined ? {} : { statusCounts: statusCountsOf(accounts, statusOf) }),
            },
        ];
        // A collapsed position keeps its line and its sum and drops its accounts.
        return isExpanded ? [...rows, ...visible.map(input.accountRowOf)] : rows;
    };

    // The rows of a list of siblings. Empty when none of them shows anything, subtotals included.
    const childRows = (children: readonly StatementNode[], isRoot: boolean): AccountsRow[] => {
        const rows: AccountsRow[] = [];
        let shown = false;
        for (const child of children) {
            if (child.kind === 'SUBTOTAL') {
                rows.push({
                    kind: 'subtotal',
                    id: child.id,
                    label: input.layoutLabelOf(child.labelKey),
                    values: input.cellsOf(valuesOf(child.id)),
                });
                continue;
            }
            const own = nodeRows(child, isRoot);
            shown = shown || own.length > NOTHING;
            rows.push(...own);
        }
        return shown ? rows : [];
    };

    const nodeRows = (node: Exclude<StatementNode, { kind: 'SUBTOTAL' }>, isRoot: boolean): AccountsRow[] => {
        if (node.kind === 'POSITION') {
            return positionRows(node.position, node.position, byPosition.get(node.position) ?? [], valuesOf(node.id));
        }

        const inner = childRows(node.children, false);
        if (inner.length === NOTHING) {
            return [];
        }
        const rows: AccountsRow[] = [];
        if (node.labelKey !== undefined) {
            const heading = input.layoutLabelOf(node.labelKey);
            rows.push(
                isRoot && input.sectionLabelOf !== undefined
                    ? { kind: 'section', id: node.id, label: input.sectionLabelOf(heading) }
                    : { kind: 'heading', id: node.id, label: heading },
            );
        }
        rows.push(...inner);
        if (node.totalLabelKey !== undefined) {
            rows.push({
                kind: 'total',
                id: `${node.id}Total`,
                label: input.layoutLabelOf(node.totalLabelKey),
                values: input.cellsOf(valuesOf(node.id)),
                level: isRoot ? 'root' : 'group',
            });
        }
        return rows;
    };

    // After the tree and in no total: the accounts that need a position first.
    return [
        ...childRows(input.layout.roots, true),
        ...positionRows(UNASSIGNED_GROUP_ID, null, unassigned, sumOfAccounts(unassigned, input.periods)),
    ];
}

/**
 * The layout with every position the accounts carry. A position the layout does not know (an older
 * backend's key) is appended at the end rather than dropped with its accounts. It is in no total.
 */
export function withUnknownPositions(layout: IStatementLayout, accounts: readonly IProjectAccount[]): IStatementLayout {
    const known = new Set<string>();
    const collect = (nodes: readonly StatementNode[]): void =>
        nodes.forEach((node) => {
            if (node.kind === 'POSITION') {
                known.add(node.position);
            } else if (node.kind === 'GROUP') {
                collect(node.children);
            }
        });
    collect(layout.roots);

    const unknown = [...accounts]
        .sort(byAccountNumber)
        .map((account) => account.accountGroup)
        .filter((position): position is string => position !== null && !known.has(position))
        .filter((position, index, all) => all.indexOf(position) === index);

    return unknown.length === NOTHING
        ? layout
        : {
              ...layout,
              roots: [
                  ...layout.roots,
                  ...unknown.map((position): StatementNode => ({ kind: 'POSITION', id: position, position })),
              ],
          };
}

/** A flat layout from a position list, for a backend that sends none. No groups, no subtotals. */
export function flatStatementLayout(
    statement: IStatementLayout['statement'],
    positions: readonly string[],
    percentBaseNodeId?: string,
): IStatementLayout {
    return {
        statement,
        roots: positions.map((position) => ({ kind: 'POSITION', id: position, position })),
        percentBaseNodeId,
    };
}

function statusCountsOf(
    accounts: readonly IProjectAccount[],
    statusOf: (account: IProjectAccount) => AccountStatus,
): AccountsStatusCounts {
    const counts: AccountsStatusCounts = { reconciled: 0, difference: 0, open: 0 };
    for (const account of accounts) {
        counts[statusOf(account)] += 1;
    }

    return counts;
}
